import React, { useState } from 'react';
import { X, Camera } from 'lucide-react';
import SectionHeader from './shared/SectionHeader';
import Reveal from './shared/Reveal';

const galleryImages = [
  '/images/office1.jpg',
  '/images/office2.JPG',
  '/images/office3.JPG',
  '/images/office4.JPG',
  '/images/office6.JPG',
  '/images/team1.JPG',
  '/images/team2.JPG',
  '/images/km001.jpeg',
  '/images/km002.jpeg',
  '/images/km003.jpeg',
  '/images/km004.jpeg',
  '/images/km005.jpeg',
  '/images/vz1.jpeg',
  '/images/vz2.jpeg',
  '/images/vz3.jpeg',
  '/images/vz4.jpeg',
  '/images/vz5.jpeg',
  '/images/vz6.jpeg',
  '/images/Guntur_XP_3.jpeg',
  '/images/Guntur_XP_5.jpeg'
];

const fallback =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEzMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzlDQTNBRiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlPC90ZXh0Pjwvc3ZnPg==';

// Split into two rows that scroll in opposite directions
const rowA = galleryImages.filter((_, i) => i % 2 === 0);
const rowB = galleryImages.filter((_, i) => i % 2 === 1);

const MarqueeRow = ({ images, reverse = false, onSelect }) => (
  <div className="relative overflow-hidden group">
    <div
      className="flex gap-4 w-max animate-marquee group-hover:[animation-play-state:paused]"
      style={reverse ? { animationDirection: 'reverse' } : undefined}
    >
      {[...images, ...images].map((image, index) => (
        <button
          key={`${image}-${index}`}
          onClick={() => onSelect(image)}
          className="relative shrink-0 w-56 h-36 sm:w-64 sm:h-40 rounded-2xl overflow-hidden cursor-pointer group/item shadow-soft"
          aria-label="Expand photo"
        >
          <img
            src={image}
            alt="Xpert Advisor offices and team"
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover/item:scale-110"
            onError={(e) => { e.target.src = fallback; }}
          />
          <span className="absolute inset-0 bg-navy-950/0 group-hover/item:bg-navy-950/40 transition-colors duration-300 flex items-center justify-center">
            <Camera className="w-6 h-6 text-white opacity-0 group-hover/item:opacity-100 transition-opacity duration-300" />
          </span>
        </button>
      ))}
    </div>
  </div>
);

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section className="py-20 lg:py-24 bg-navy-50/50 overflow-hidden">
      <div className="container-x">
        <SectionHeader
          eyebrow="Inside Xpert Advisor"
          title={<>Our offices, our people, <span className="text-brand-500">your team</span></>}
          subtitle="Four branches across Telangana and Andhra Pradesh — drop in for a coffee and a counseling session."
        />
      </div>

      <Reveal className="space-y-4 relative">
        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-32 bg-gradient-to-r from-navy-50/90 to-transparent z-10"></div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-32 bg-gradient-to-l from-navy-50/90 to-transparent z-10"></div>

        <MarqueeRow images={rowA} onSelect={setSelectedImage} />
        <MarqueeRow images={rowB} reverse onSelect={setSelectedImage} />
      </Reveal>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-navy-950/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-fade-in [animation-duration:0.25s]"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={selectedImage}
              alt="Expanded view"
              className="w-full h-full object-contain rounded-2xl shadow-lift"
              onError={(e) => { e.target.src = fallback; }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
